import React, { useState, useEffect } from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import { Lock, UserPlus, Settings, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import MessageList from './MessageList';
import MessageComposer from './MessageComposer';
import InviteMemberDialog from './InviteMemberDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface ChatAreaProps {
  showDetails: boolean;
  onToggleDetails: () => void;
  isGlobalDarkMode: boolean;
  isMobile?: boolean;
  onOpenSidebar?: () => void;
}

const ChatArea: React.FC<ChatAreaProps> = ({ 
  showDetails, 
  onToggleDetails, 
  isGlobalDarkMode, 
  isMobile = false,
  onOpenSidebar 
}) => {
  const {
    activeChannelId,
    activeDmId,
    channels,
    directMessages,
    getChannelMessages,
    getDmMessages,
    getDmParticipant,
    setActiveChannel,
    setActiveDm,
    isChannelMember,
    activeThreadId,
  } = useTeamChat();

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const activeChannel = channels.find(c => c.id === activeChannelId);
  const activeDm = directMessages.find(d => d.id === activeDmId);
  const dmParticipant = activeDm ? getDmParticipant(activeDm) : undefined;

  // Check if user is a member of the channel
  const isMember = activeChannelId ? isChannelMember(activeChannelId) : false;

  const messages = activeChannelId
    ? getChannelMessages(activeChannelId)
    : activeDmId
      ? getDmMessages(activeDmId)
      : [];

  const bgColor = isGlobalDarkMode ? 'bg-background' : 'bg-white';
  const headerBg = isGlobalDarkMode ? 'bg-background/50' : 'bg-white/50';
  const borderColor = isGlobalDarkMode ? 'border-border' : 'border-gray-200';
  const textColor = isGlobalDarkMode ? 'text-foreground' : 'text-gray-900';

  if (!activeChannelId && !activeDmId) {
    const hasNoConversations = channels.length === 0 && directMessages.length === 0;

    return (
      <div className={cn("h-full flex-1 flex flex-col items-center justify-center gap-4 p-6", bgColor)}>
        {hasNoConversations ? (
          <>
            <p className="text-muted-foreground text-center">No groups or conversations yet</p>
            {isMobile && onOpenSidebar && (
              <Button
                onClick={onOpenSidebar}
                className="bg-workspace-accent hover:bg-workspace-accent-hover text-white"
              >
                Get Started
              </Button>
            )}
          </>
        ) : (
          <p className="text-muted-foreground text-center">Select a group or conversation to start messaging</p>
        )}
      </div>
    );
  }

  const handleBackToInbox = () => {
    setActiveChannel(null);
    setActiveDm(null);
  };

  const hasRightPanel = showDetails || !!activeThreadId;

  return (
    <div className={cn("flex-1 flex flex-col min-w-0 h-full", bgColor)}>
      {/* Header - Fixed */}
      <div className={cn("h-12 pl-6 md:pl-10 flex items-center justify-between border-b backdrop-blur-sm flex-shrink-0 z-10", hasRightPanel ? "pr-4" : "pr-6 md:pr-10", headerBg, borderColor)}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToInbox}
              className={cn(
                "p-2 h-8 w-8 flex-shrink-0",
                isGlobalDarkMode
                  ? "hover:bg-card text-muted-foreground hover:text-foreground"
                  : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
              )}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          {activeChannel && (
            <>
              <Avatar className="w-7 h-7 flex-shrink-0">
                <AvatarImage src={activeChannel.avatar} alt={activeChannel.name} />
                <AvatarFallback className={cn("text-xs text-white", activeChannel.avatarColor || "bg-slate-500")}>
                  {activeChannel.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className={cn("font-bold truncate", textColor)}>{activeChannel.name}</span>
              {activeChannel.isPrivate && (
                <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}
            </>
          )}
          {activeDm && dmParticipant && (
            <>
              <Avatar className="w-7 h-7 flex-shrink-0">
                <AvatarImage src={dmParticipant.avatar} alt={dmParticipant.displayName} />
                <AvatarFallback className={isGlobalDarkMode ? "text-xs bg-card text-muted-foreground" : "text-xs bg-slate-200 text-slate-700"}>
                  {dmParticipant.displayName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className={cn("font-bold truncate", textColor)}>{dmParticipant.displayName}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {activeChannel && isMember && (
            <InviteMemberDialog
              open={inviteDialogOpen}
              onOpenChange={setInviteDialogOpen}
              channelId={activeChannel.id}
              channelName={activeChannel.name}
              currentMembers={activeChannel.members}
              isGlobalDarkMode={isGlobalDarkMode}
              isMobile={isSmallScreen}
            >
              {isSmallScreen ? (
                /* Mobile: Icon-only button */
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 w-7 p-0 transition-colors",
                    isGlobalDarkMode
                      ? "text-muted-foreground hover:bg-card hover:text-foreground"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  )}
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
              ) : (
                /* Desktop: Full button with text */
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "gap-2 h-7 border-workspace-accent/20 transition-all",
                    isGlobalDarkMode
                      ? "bg-card/50 hover:bg-card text-muted-foreground border-border"
                      : "bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900"
                  )}
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Invite</span>
                </Button>
              )}
            </InviteMemberDialog>
          )}
          <Button
            variant={showDetails ? "secondary" : "ghost"}
            size="sm"
            className={cn(
              "h-7 w-7 p-0 transition-colors",
              showDetails
                ? (isGlobalDarkMode ? "bg-card text-foreground" : "bg-muted text-gray-900")
                : (isGlobalDarkMode ? "text-muted-foreground hover:bg-card hover:text-foreground" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900")
            )}
            onClick={onToggleDetails}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages - Scrollable */}
      <MessageList messages={messages} isGlobalDarkMode={isGlobalDarkMode} hasRightPanel={hasRightPanel} />

      {/* Composer - Fixed */}
      <MessageComposer
        channelId={activeChannelId || undefined}
        dmId={activeDmId || undefined}
        isGlobalDarkMode={isGlobalDarkMode}
        isMobile={isMobile}
        hasRightPanel={hasRightPanel}
      />
    </div>
  );
};

export default ChatArea;
